import {
  type MotivoDeBloqueio,
  type StatusDaCotacao,
  type StatusDaWaitlist,
  type StatusDoPrestador,
  type TipoDePrestador,
  cotacaoEmitidaSchema,
  disponibilidade,
} from '@locaveri/shared'
import {
  type QueryClient,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api'
import { ROTAS_DE_COTACOES, ROTAS_DE_DISPONIBILIDADE } from '@/lib/endpoints'
import { montarQuery } from '@/lib/query-string'

import {
  CHAVE_DE_BLOQUEIOS,
  CHAVE_DE_CALENDARIO,
  CHAVE_DE_CAPACIDADE,
  CHAVE_DE_COTACOES,
  CHAVE_DE_ESCALA,
  CHAVE_DE_OVERBOOKING,
  CHAVE_DE_PRESTADORES,
  CHAVE_DE_WAITLIST,
  ORIGENS_DA_TOLERANCIA,
  paginaDe,
} from './api'

/**
 * Cotação, disponibilidade, calendário, lista de espera, overbooking e
 * prestadores.
 *
 * ---------------------------------------------------------------------------
 * O PAINEL NUNCA RECALCULA UM TOTAL
 * ---------------------------------------------------------------------------
 * A cotação chega CONGELADA: total, detalhamento linha a linha, dia a dia, a
 * tolerância que valia e o hash SHA-256 das regras aplicadas. O painel VALIDA o
 * que recebeu — `cotacaoEmitidaSchema` vem do pacote compartilhado, é o mesmo
 * espelho que o app usa — e exibe. Refazer a soma aqui é exatamente como duas
 * telas passam a discordar sobre o preço; e o `hashDasRegras` existe para
 * responder, meses depois, qual versão da tabela gerou aquele número.
 *
 * Por isso as linhas do breakdown SOMAM: desconto vem negativo, e não como um
 * valor positivo a subtrair. Uma tela que subtraísse o cupom estaria aplicando
 * o desconto duas vezes.
 *
 * ---------------------------------------------------------------------------
 * COTAR ESCREVE
 * ---------------------------------------------------------------------------
 * `POST /cotacoes` é 201 e não 200 porque cada categoria cotável vira um
 * `QuoteSnapshot`. E é 201 mesmo quando NADA está disponível: "não há carro",
 * "o cupom venceu" e "esta categoria não tem tarifa" são leitura do mercado no
 * período, não falhas da requisição. Nenhuma indisponibilidade vira erro de
 * tela — cada uma vem nomeada, por categoria e por extra.
 *
 * ---------------------------------------------------------------------------
 * CAPACIDADE É POR CATEGORIA
 * ---------------------------------------------------------------------------
 * A regra de ouro vale aqui também: `capacidade` e `disponiveis` contam
 * VEÍCULOS de uma categoria, e a placa só é decidida na alocação. Os dois campos
 * vêm separados de propósito — "2 de 8" e "2 de 2" são a mesma disponibilidade e
 * duas decisões comerciais completamente diferentes.
 *
 * As chaves de cache moram em `./api`, e a importação é de mão única: salvar uma
 * tarifa invalida a cotação, e chaves espalhadas fariam os dois módulos
 * importarem um do outro.
 */

// ---------------------------------------------------------------------------
// Vocabulário que só a API descreve
// ---------------------------------------------------------------------------

/**
 * Por que a categoria não pôde ser cotada.
 *
 * Quatro motivos e não um booleano, porque são quatro decisões diferentes para
 * quem lê: `SEM_FROTA` é compra de carro, `SEM_UNIDADE_LIVRE` é lista de espera
 * (ou outra data), e os dois de preço são cadastro faltando — problema que se
 * resolve em dois minutos e que, escondido atrás de um "indisponível" genérico,
 * ficaria meses sem ninguém notar.
 */
export const MOTIVOS_DE_INDISPONIBILIDADE = [
  'SEM_FROTA',
  'SEM_UNIDADE_LIVRE',
  'SEM_TABELA_DE_PRECO',
  'SEM_TARIFA',
] as const

export type MotivoDeIndisponibilidade = (typeof MOTIVOS_DE_INDISPONIBILIDADE)[number]

export const ROTULO_DO_MOTIVO_DE_INDISPONIBILIDADE: Readonly<
  Record<MotivoDeIndisponibilidade, string>
> = {
  SEM_FROTA: 'Sem frota',
  SEM_UNIDADE_LIVRE: 'Sem unidade livre',
  SEM_TABELA_DE_PRECO: 'Sem tabela de preço',
  SEM_TARIFA: 'Sem tarifa para a faixa',
}

/** A frase que diz o que FAZER a respeito — é o que a tela mostra embaixo do selo. */
export const ACAO_DO_MOTIVO_DE_INDISPONIBILIDADE: Readonly<
  Record<MotivoDeIndisponibilidade, string>
> = {
  SEM_FROTA: 'Não há veículo desta categoria na frota ativa.',
  SEM_UNIDADE_LIVRE: 'Toda a frota está ocupada no período. Ofereça a lista de espera.',
  SEM_TABELA_DE_PRECO: 'Nenhuma tabela vigente cobre este período — confira a vigência.',
  SEM_TARIFA: 'A categoria não tem tarifa para esta faixa de duração. Precifique a faixa.',
}

/**
 * Por que um extra pedido não entrou na cotação.
 *
 * O extra recusado NUNCA some da resposta em silêncio: omiti-lo faria o total
 * ficar menor do que o cliente pediu sem nada na tela explicando por quê — e
 * `SEM_PRESTADOR`, o caso mais comum, é justamente aquele em que a locadora quer
 * ligar oferecendo outra data.
 */
export const MOTIVOS_DE_RECUSA_DE_EXTRA = [
  'NAO_ENCONTRADO',
  'INATIVO',
  'ACIMA_DA_QUANTIDADE_MAXIMA',
  'SEM_PRESTADOR',
] as const

export type MotivoDeRecusaDeExtra = (typeof MOTIVOS_DE_RECUSA_DE_EXTRA)[number]

export const ROTULO_DO_MOTIVO_DE_RECUSA_DE_EXTRA: Readonly<Record<MotivoDeRecusaDeExtra, string>> =
  {
    NAO_ENCONTRADO: 'Extra não encontrado',
    INATIVO: 'Extra desativado',
    ACIMA_DA_QUANTIDADE_MAXIMA: 'Acima da quantidade máxima',
    SEM_PRESTADOR: 'Sem prestador livre',
  }

/**
 * Por que o cupom foi recusado.
 *
 * É o vocabulário do módulo de cupons, espelhado aqui porque o ENVELOPE da
 * cotação carrega o veredito — a tela de cotação precisa dele sem depender da
 * tela de cupons. `AINDA_NAO_VIGENTE` e `VENCIDO` são separados de propósito: o
 * primeiro é campanha que vai começar (o atendente pode segurar a reserva), o
 * segundo é campanha que acabou.
 */
export const MOTIVOS_DE_RECUSA_DE_CUPOM = [
  'NAO_ENCONTRADO',
  'INATIVO',
  'AINDA_NAO_VIGENTE',
  'VENCIDO',
  'ESGOTADO',
  'LIMITE_DA_CONTA_ATINGIDO',
] as const

export type MotivoDeRecusaDeCupom = (typeof MOTIVOS_DE_RECUSA_DE_CUPOM)[number]

export const ROTULO_DO_MOTIVO_DE_RECUSA_DE_CUPOM: Readonly<Record<MotivoDeRecusaDeCupom, string>> =
  {
    NAO_ENCONTRADO: 'Código não encontrado',
    INATIVO: 'Cupom desligado',
    AINDA_NAO_VIGENTE: 'Campanha ainda não começou',
    VENCIDO: 'Campanha encerrada',
    ESGOTADO: 'Cupom esgotado',
    LIMITE_DA_CONTA_ATINGIDO: 'Limite desta conta atingido',
  }

// ---------------------------------------------------------------------------
// Cotação — leitura
// ---------------------------------------------------------------------------

export const extraRecusadoSchema = z.object({
  extraId: z.uuid(),
  /** `null` quando o id não corresponde a extra nenhum — não há nome a dizer. */
  nome: z.string().nullable(),
  quantidade: z.number().int().positive(),
  motivo: z.enum(MOTIVOS_DE_RECUSA_DE_EXTRA),
  /** Frase pronta para a tela, já em pt-BR e já vinda do servidor. */
  detalhe: z.string(),
})

export type ExtraRecusado = z.infer<typeof extraRecusadoSchema>

/**
 * O veredito do cupom, uma vez por cotação.
 *
 * Fica no envelope e não em cada categoria porque o código é um só e o motivo da
 * recusa não muda de categoria para categoria. A única condição que DEPENDE do
 * total é o piso `valorMinimoDaReserva`, e essa varia — por isso o piso viaja
 * aqui (para a tela escrever "válido a partir de R$ 500") e o veredito por
 * categoria vive em `cupomAplicado`.
 */
export const vereditoDoCupomSchema = z.object({
  codigo: z.string(),
  aceito: z.boolean(),
  /** `null` exatamente quando `aceito` é `true`. */
  motivo: z.enum(MOTIVOS_DE_RECUSA_DE_CUPOM).nullable(),
  /** Centavos como string. `null` = sem piso. */
  valorMinimoDaReserva: z.string().nullable(),
})

export type VereditoDoCupom = z.infer<typeof vereditoDoCupomSchema>

/**
 * Uma categoria no resultado da cotação.
 *
 * `diarias` e a tolerância aparecem aqui E dentro de `cotacao`, e a duplicação é
 * deliberada: fora, porque a categoria INDISPONÍVEL também precisa dizer
 * quantas diárias o período daria (é o que o cliente compara com a próxima
 * data); dentro, porque o snapshot tem de ser autossuficiente — a Fase 4 fatura
 * lendo só ele.
 */
export const categoriaCotadaSchema = z.object({
  categoryId: z.uuid(),
  slug: z.string(),
  nome: z.string(),

  capacidade: z.number().int().nonnegative(),
  disponiveis: z.number().int().nonnegative(),
  /** Quantos a mais a política permite vender. Zero = overbooking desligado. */
  overbookingDisponivel: z.number().int().nonnegative(),

  disponivel: z.boolean(),
  /** `null` exatamente quando `disponivel` é `true`. */
  motivo: z.enum(MOTIVOS_DE_INDISPONIBILIDADE).nullable(),
  /** `true` quando a venda só cabe pela margem de overbooking. */
  emOverbooking: z.boolean(),
  /** `true` quando vale oferecer a lista de espera para esta categoria. */
  listaDeEspera: z.boolean(),

  diarias: z.number().int().positive(),
  toleranciaEmMinutos: z.number().int().nonnegative(),
  origemDaTolerancia: z.enum(ORIGENS_DA_TOLERANCIA),

  /** Qual tabela precificou. `null` quando nenhuma cobria a categoria. */
  priceTableId: z.uuid().nullable(),
  /** `true` quando o preço veio da tabela negociada da conta. */
  tabelaNegociada: z.boolean(),

  extrasRecusados: z.array(extraRecusadoSchema),
  /**
   * `false` quando o cupom foi aceito no geral mas o total DESTA categoria não
   * alcançou o piso — a única recusa que varia de categoria para categoria.
   */
  cupomAplicado: z.boolean(),

  /** A cotação congelada. `null` quando a categoria não pôde ser cotada. */
  cotacao: cotacaoEmitidaSchema.nullable(),
})

export type CategoriaCotada = z.infer<typeof categoriaCotadaSchema>

/**
 * O resultado de `POST /cotacoes`.
 *
 * `avaliadoEm` é o instante que a regra de antecedência usou: sem ele, duas
 * cotações do mesmo período com preços diferentes seriam inexplicáveis.
 */
export const resultadoDaCotacaoSchema = z.object({
  retirada: z.string(),
  devolucao: z.string(),
  timeZone: z.string(),
  avaliadoEm: z.string(),
  /** Buffer de limpeza e vistoria já somado nas duas pontas da janela. */
  bufferEmHoras: z.number().int().nonnegative(),
  /** Quando as cotações emitidas nesta resposta expiram. */
  expiraEm: z.string(),
  /** `null` quando nenhum cupom foi informado. */
  cupom: vereditoDoCupomSchema.nullable(),
  itens: z.array(categoriaCotadaSchema),
})

export type ResultadoDaCotacao = z.infer<typeof resultadoDaCotacaoSchema>

/** A cotação congelada, como `GET /cotacoes/:id` a devolve. */
export const cotacaoSchema = cotacaoEmitidaSchema

export type Cotacao = z.infer<typeof cotacaoSchema>

// ---------------------------------------------------------------------------
// Disponibilidade — leitura
// ---------------------------------------------------------------------------

export const capacidadeDaCategoriaSchema = z.object({
  categoryId: z.uuid(),
  nome: z.string(),
  capacidade: z.number().int().nonnegative(),
  disponiveis: z.number().int().nonnegative(),
  overbookingDisponivel: z.number().int().nonnegative(),
})

export type CapacidadeDaCategoria = z.infer<typeof capacidadeDaCategoriaSchema>

/**
 * A resposta de capacidade.
 *
 * `janelaDe`/`janelaAte` são o período JÁ dilatado pelo buffer, e voltam ecoados
 * porque é a única forma de a tela explicar por que um carro que devolve às 10h
 * não aparece livre para as 10h05. Sem isso, a operação abre um chamado.
 */
export const capacidadeSchema = z.object({
  retirada: z.string(),
  devolucao: z.string(),
  bufferEmHoras: z.number().int().nonnegative(),
  janelaDe: z.string(),
  janelaAte: z.string(),
  itens: z.array(capacidadeDaCategoriaSchema),
})

export type Capacidade = z.infer<typeof capacidadeSchema>

/**
 * O calendário dia a dia. `dias` reusa o contrato compartilhado — é o mesmo que
 * o app do cliente lê, e `diaria` vem `null` deste endpoint: ele conta FROTA, e
 * inventar um preço para preencher o campo violaria o invariante nº 3.
 */
export const calendarioSchema = z.object({
  categoryId: z.uuid(),
  de: z.string(),
  ate: z.string(),
  dias: z.array(disponibilidade.diaDoCalendarioSchema),
})

export type Calendario = z.infer<typeof calendarioSchema>

/**
 * Quantos prestadores de cada tipo estão livres.
 *
 * Os dois tipos SEMPRE aparecem, mesmo zerados: a ausência de uma linha seria
 * ambígua entre "não há segurança livre" e "não há segurança cadastrado", e é a
 * primeira leitura que decide se a cotação pode oferecer o extra.
 */
export const prestadoresLivresDoTipoSchema = z.object({
  tipo: disponibilidade.tipoDePrestadorSchema,
  /** Prestadores ativos que atendem a base pedida. */
  total: z.number().int().nonnegative(),
  livres: z.number().int().nonnegative(),
})

export const prestadoresLivresSchema = z.object({
  retirada: z.string(),
  devolucao: z.string(),
  itens: z.array(prestadoresLivresDoTipoSchema),
})

export type PrestadoresLivres = z.infer<typeof prestadoresLivresSchema>

// ---------------------------------------------------------------------------
// Bloqueio de veículo
// ---------------------------------------------------------------------------

/**
 * Um bloqueio de veículo.
 *
 * `inicio` e `fim` são INSTANTES, nunca datas puras: a limpeza vale das 10h às
 * 13h, e truncar para o dia faria a disponibilidade perder meia frota por dia.
 * `categoryId` vem junto porque a tela de disponibilidade é por categoria — sem
 * ele, cada linha exigiria uma segunda consulta à frota.
 */
export const bloqueioDeVeiculoSchema = z.object({
  id: z.uuid(),
  vehicleId: z.uuid(),
  veiculo: z.object({
    placa: z.string(),
    marca: z.string(),
    modelo: z.string(),
    categoryId: z.uuid(),
  }),
  motivo: disponibilidade.motivoDeBloqueioSchema,
  inicio: z.string(),
  fim: z.string(),
  observacoes: z.string().nullable(),
  /** Preenchido a partir da Fase 4, quando o bloqueio nasce de uma reserva. */
  reservationId: z.uuid().nullable(),
  /** Nulo quando o bloqueio nasceu da reserva, e não de uma pessoa. */
  criadoPorId: z.uuid().nullable(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type BloqueioDeVeiculo = z.infer<typeof bloqueioDeVeiculoSchema>

export const paginaDeBloqueiosSchema = paginaDe(bloqueioDeVeiculoSchema)

export type PaginaDeBloqueios = z.infer<typeof paginaDeBloqueiosSchema>

export const bloqueioDePrestadorSchema = z.object({
  id: z.uuid(),
  serviceProviderId: z.uuid(),
  prestador: z.object({
    nome: z.string(),
    tipo: disponibilidade.tipoDePrestadorSchema,
  }),
  motivo: disponibilidade.motivoDeBloqueioSchema,
  inicio: z.string(),
  fim: z.string(),
  observacoes: z.string().nullable(),
  reservationId: z.uuid().nullable(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type BloqueioDePrestador = z.infer<typeof bloqueioDePrestadorSchema>

export const paginaDeEscalaSchema = paginaDe(bloqueioDePrestadorSchema)

export type PaginaDeEscala = z.infer<typeof paginaDeEscalaSchema>

// ---------------------------------------------------------------------------
// Prestador
// ---------------------------------------------------------------------------

/**
 * Um prestador.
 *
 * `custoDiario` é o que a OPERAÇÃO paga por diária, em centavos — não é o preço
 * ao cliente, que vem do `Extra`. Manter os dois separados é o que torna a
 * margem do serviço visível no relatório de rentabilidade.
 *
 * `locationId` nulo significa "atende de qualquer base", e não "sem base": é a
 * diferença entre um motorista coringa e um cadastro incompleto, e a consulta de
 * prestadores livres conta o primeiro em toda base.
 */
export const prestadorSchema = z.object({
  id: z.uuid(),
  nome: z.string(),
  tipo: disponibilidade.tipoDePrestadorSchema,
  telefone: z.string().nullable(),
  email: z.string().nullable(),
  /** Só dígitos, como está gravado. A máscara é da tela. */
  cpf: z.string().nullable(),
  /**
   * Centavos como string decimal (o wire de `Money`), ou `null`.
   *
   * ⚠️ `null` é "você não pode ver", não "custo zero". O campo é gateado por
   * `service_provider.cost` na API — OPERACIONAL e ATENDIMENTO chegam à tela de
   * escala e NÃO recebem o valor. Sem o `.nullable()` aqui, o Zod derrubaria a
   * listagem inteira para esses dois papéis em vez de esconder uma coluna.
   */
  custoDiario: z.string().nullable(),
  locationId: z.uuid().nullable(),
  status: disponibilidade.statusDoPrestadorSchema,
  observacoes: z.string().nullable(),
  /** Quantos bloqueios de escala o prestador tem. */
  bloqueios: z.number().int().nonnegative(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Prestador = z.infer<typeof prestadorSchema>

export const paginaDePrestadoresSchema = paginaDe(prestadorSchema)

export type PaginaDePrestadores = z.infer<typeof paginaDePrestadoresSchema>

// ---------------------------------------------------------------------------
// Lista de espera e overbooking
// ---------------------------------------------------------------------------

/**
 * Um item da demanda reprimida.
 *
 * `nome`, `email` e `telefone` viajam porque quem vai LIGAR de volta precisa
 * deles — é para isso que a fila existe. A intenção vale mesmo sem cadastro, e
 * por isso os três são anuláveis; o que o servidor garante é que ao menos um
 * contato existe.
 */
export const itemDeWaitlistSchema = z.object({
  id: z.uuid(),
  categoryId: z.uuid(),
  categoriaNome: z.string(),
  accountId: z.uuid().nullable(),
  retirada: z.string(),
  devolucao: z.string(),
  nome: z.string().nullable(),
  email: z.string().nullable(),
  telefone: z.string().nullable(),
  status: disponibilidade.statusDaWaitlistSchema,
  notificadoEm: z.string().nullable(),
  observacoes: z.string().nullable(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type ItemDeWaitlist = z.infer<typeof itemDeWaitlistSchema>

export const paginaDeWaitlistSchema = paginaDe(itemDeWaitlistSchema)

export type PaginaDeWaitlist = z.infer<typeof paginaDeWaitlistSchema>

/**
 * Um overbooking exercido, com o custo do upgrade.
 *
 * `custoDoUpgrade` pode ser NEGATIVO, e o sinal é a informação: positivo é
 * prejuízo (entregamos algo mais caro do que vendemos), negativo é a rara vez em
 * que a política se pagou. A string de centavos aceita o `-` justamente por
 * isso — não trate o sinal como erro de formato.
 */
export const overbookingSchema = z.object({
  id: z.uuid(),
  categoriaVendidaId: z.uuid(),
  categoriaVendidaNome: z.string(),
  categoriaEntregueId: z.uuid().nullable(),
  categoriaEntregueNome: z.string().nullable(),
  retirada: z.string(),
  devolucao: z.string(),
  custoDoUpgrade: z.string(),
  reservationId: z.uuid().nullable(),
  observacoes: z.string().nullable(),
  criadoEm: z.string(),
})

export type Overbooking = z.infer<typeof overbookingSchema>

/**
 * A página de overbookings MAIS o custo do recorte filtrado inteiro.
 *
 * O total NÃO é a soma da página. Somar as vinte linhas visíveis no navegador
 * responderia "quanto a política custou nesta página", e uma margem aproximada
 * não decide se a política se paga — além de exigir aritmética monetária no
 * cliente, que o invariante nº 1 proíbe.
 */
export const paginaDeOverbookingSchema = paginaDe(overbookingSchema).extend({
  custoTotalDoUpgrade: z.string(),
})

export type PaginaDeOverbooking = z.infer<typeof paginaDeOverbookingSchema>

// ---------------------------------------------------------------------------
// Corpos de escrita e pedidos
// ---------------------------------------------------------------------------

/**
 * Corpo já validado por um schema de `@locaveri/shared`, pronto para o fio.
 *
 * Mapa aberto pelo mesmo motivo de `CorpoDeTabelaDePreco`: os schemas de entrada
 * TRANSFORMAM — `instanteSchema` devolve `Date` e `moneySchema` devolve
 * `bigint`, que `JSON.stringify` nem serializa. O formulário valida com
 * `safeParse` e envia a ENTRADA validada, nunca `validado.data`.
 */
type CorpoValidado = Readonly<Record<string, unknown>>

export type CorpoDeBloqueio = CorpoValidado
export type CorpoDePrestador = CorpoValidado
export type CorpoDeItemDeWaitlist = CorpoValidado
export type CorpoDeOverbooking = CorpoValidado

export interface ExtraDoPedido {
  readonly extraId: string
  readonly quantidade: number
}

/**
 * O pedido de cotação do PAINEL.
 *
 * `categoryId` é opcional aqui e obrigatório no `pedirCotacaoSchema` do pacote
 * compartilhado, e a diferença é de propósito: aquele é o contrato do APP, que
 * só cota depois de o cliente escolher a classe; o painel faz a outra pergunta —
 * "quanto custa CADA categoria neste período?". Relaxar o schema compartilhado
 * atenderia os dois e criaria um terceiro problema: o app poderia mandar um
 * pedido sem categoria por engano e receber a vitrine inteira em vez do erro de
 * validação que espera.
 *
 * Nada aqui manda preço, e é deliberado: um campo de valor nesta entrada seria
 * uma porta para o preço vir de fora (invariante nº 3).
 */
export interface PedidoDeCotacao {
  /** Ausente = cota TODAS as categorias ativas. */
  readonly categoryId?: string | undefined
  /** ISO 8601 COM fuso — a API recusa string sem deslocamento. */
  readonly retirada: string
  readonly devolucao: string
  readonly retiradaLocalId?: string | undefined
  readonly devolucaoLocalId?: string | undefined
  /** Cotar em nome de um cliente corporativo é o que puxa a tabela negociada. */
  readonly accountId?: string | undefined
  readonly extras?: readonly ExtraDoPedido[] | undefined
  /** O CÓDIGO digitado, não o id: é o que o cliente tem na mão. */
  readonly cupom?: string | undefined
}

// ---------------------------------------------------------------------------
// Filtros e parâmetros de consulta
// ---------------------------------------------------------------------------

/**
 * O período de uma consulta de disponibilidade. Vazio nas duas pontas mantém a
 * query desligada — com uma ponta só a API responderia 422 e a tela mostraria
 * erro de validação para quem ainda está preenchendo.
 */
export interface ParametrosDeCapacidade {
  readonly retirada: string
  readonly devolucao: string
  readonly categoryId: string
  readonly retiradaLocalId: string
  readonly devolucaoLocalId: string
}

export interface ParametrosDeCalendario {
  readonly categoryId: string
  /** Datas de CALENDÁRIO (`AAAA-MM-DD`) — o cliente escolhe dia, não hora. */
  readonly de: string
  readonly ate: string
  readonly locationId: string
}

export interface ParametrosDePrestadoresLivres {
  readonly retirada: string
  readonly devolucao: string
  readonly tipo: TipoDePrestador | ''
  readonly locationId: string
}

export interface FiltrosDeBloqueios {
  readonly pagina: number
  readonly tamanho: number
  readonly vehicleId: string
  readonly serviceProviderId: string
  readonly categoryId: string
  readonly motivo: MotivoDeBloqueio | ''
  /** Recorte de período: tudo que COLIDE com a janela, não só o que começa nela. */
  readonly de: string
  readonly ate: string
}

export interface FiltrosDePrestadores {
  readonly pagina: number
  readonly tamanho: number
  readonly tipo: TipoDePrestador | ''
  readonly status: StatusDoPrestador | ''
  readonly locationId: string
  /** Só quem está livre no período INTEIRO — a pergunta que a escala faz. */
  readonly livreDe: string
  readonly livreAte: string
}

export interface FiltrosDeWaitlist {
  readonly pagina: number
  readonly tamanho: number
  readonly categoryId: string
  readonly accountId: string
  readonly status: StatusDaWaitlist | ''
  /** Recorte por data de retirada pretendida. */
  readonly de: string
  readonly ate: string
}

export interface FiltrosDeOverbooking {
  readonly pagina: number
  readonly tamanho: number
  readonly categoriaVendidaId: string
  readonly de: string
  readonly ate: string
}

// ---------------------------------------------------------------------------
// Invalidação
// ---------------------------------------------------------------------------

/**
 * Toda escrita de ocupação muda o que está disponível.
 *
 * Um bloqueio novo tira uma unidade da categoria no período, e isso aparece em
 * três lugares: a capacidade, o calendário e a cotação que a tela acabou de
 * pedir. Invalidar só a lista de bloqueios deixaria o atendente vender um carro
 * que a manutenção acabou de tomar — que é o buraco que a Fase 3 existe para
 * fechar.
 */
function invalidarDisponibilidade(queryClient: QueryClient, ...chaves: readonly string[]): void {
  for (const chave of chaves) {
    void queryClient.invalidateQueries({ queryKey: [chave] })
  }

  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CAPACIDADE] })
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CALENDARIO] })
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_COTACOES] })
}

// ---------------------------------------------------------------------------
// Cotação
// ---------------------------------------------------------------------------

/**
 * Cota o período. É MUTAÇÃO e não consulta, mesmo parecendo uma pergunta: a
 * chamada grava um `QuoteSnapshot` por categoria cotável, e um `useQuery`
 * refetching sozinho no foco da janela emitiria cotações que ninguém pediu.
 */
export function useCotar(): UseMutationResult<ResultadoDaCotacao, Error, PedidoDeCotacao> {
  return useMutation({
    mutationFn: (pedido: PedidoDeCotacao) =>
      apiPost(ROTAS_DE_COTACOES.lista, resultadoDaCotacaoSchema, pedido),
  })
}

/**
 * Uma cotação congelada. Só cai no cache quando há id — a tela de detalhe é
 * aberta a partir de um resultado, não em branco.
 */
export function useCotacao(id: string | null): UseQueryResult<Cotacao> {
  return useQuery({
    queryKey: [CHAVE_DE_COTACOES, id],
    queryFn: ({ signal }) => apiGet(ROTAS_DE_COTACOES.porId(id ?? ''), cotacaoSchema, { signal }),
    enabled: id !== null,
  })
}

// ---------------------------------------------------------------------------
// Disponibilidade
// ---------------------------------------------------------------------------

export function useCapacidade(parametros: ParametrosDeCapacidade): UseQueryResult<Capacidade> {
  return useQuery({
    queryKey: [CHAVE_DE_CAPACIDADE, parametros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        retirada: parametros.retirada,
        devolucao: parametros.devolucao,
        categoryId: parametros.categoryId,
        retiradaLocalId: parametros.retiradaLocalId,
        devolucaoLocalId: parametros.devolucaoLocalId,
      })

      return apiGet(`${ROTAS_DE_DISPONIBILIDADE.capacidade}${query}`, capacidadeSchema, { signal })
    },
    enabled: parametros.retirada.length > 0 && parametros.devolucao.length > 0,
    placeholderData: (anterior) => anterior,
  })
}

export function useCalendario(parametros: ParametrosDeCalendario): UseQueryResult<Calendario> {
  return useQuery({
    queryKey: [CHAVE_DE_CALENDARIO, parametros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        categoryId: parametros.categoryId,
        de: parametros.de,
        ate: parametros.ate,
        locationId: parametros.locationId,
      })

      return apiGet(`${ROTAS_DE_DISPONIBILIDADE.calendario}${query}`, calendarioSchema, { signal })
    },
    // O calendário é POR CATEGORIA: sem ela não há pergunta a fazer.
    enabled:
      parametros.categoryId.length > 0 && parametros.de.length > 0 && parametros.ate.length > 0,
    placeholderData: (anterior) => anterior,
  })
}

export function usePrestadoresLivres(
  parametros: ParametrosDePrestadoresLivres,
): UseQueryResult<PrestadoresLivres> {
  return useQuery({
    queryKey: [CHAVE_DE_PRESTADORES, 'livres', parametros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        retirada: parametros.retirada,
        devolucao: parametros.devolucao,
        tipo: parametros.tipo,
        locationId: parametros.locationId,
      })

      return apiGet(
        `${ROTAS_DE_DISPONIBILIDADE.prestadoresLivres}${query}`,
        prestadoresLivresSchema,
        { signal },
      )
    },
    enabled: parametros.retirada.length > 0 && parametros.devolucao.length > 0,
    placeholderData: (anterior) => anterior,
  })
}

// ---------------------------------------------------------------------------
// Bloqueios de veículo
// ---------------------------------------------------------------------------

export function useBloqueios(filtros: FiltrosDeBloqueios): UseQueryResult<PaginaDeBloqueios> {
  return useQuery({
    queryKey: [CHAVE_DE_BLOQUEIOS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        vehicleId: filtros.vehicleId,
        categoryId: filtros.categoryId,
        motivo: filtros.motivo,
        de: filtros.de,
        ate: filtros.ate,
      })

      return apiGet(`${ROTAS_DE_DISPONIBILIDADE.bloqueios}${query}`, paginaDeBloqueiosSchema, {
        signal,
      })
    },
    placeholderData: (anterior) => anterior,
  })
}

export function useCriarBloqueio(): UseMutationResult<BloqueioDeVeiculo, Error, CorpoDeBloqueio> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeBloqueio) =>
      apiPost(ROTAS_DE_DISPONIBILIDADE.bloqueios, bloqueioDeVeiculoSchema, dados),
    onSuccess: () => {
      invalidarDisponibilidade(queryClient, CHAVE_DE_BLOQUEIOS)
    },
  })
}

export interface AtualizacaoDeBloqueio {
  readonly id: string
  /** Motivo, início, fim e observação. O VEÍCULO não é editável. */
  readonly dados: CorpoDeBloqueio
}

export function useAtualizarBloqueio(): UseMutationResult<
  BloqueioDeVeiculo,
  Error,
  AtualizacaoDeBloqueio
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeBloqueio) =>
      apiPatch(ROTAS_DE_DISPONIBILIDADE.bloqueioPorId(id), bloqueioDeVeiculoSchema, dados),
    onSuccess: () => {
      invalidarDisponibilidade(queryClient, CHAVE_DE_BLOQUEIOS)
    },
  })
}

/**
 * Libera o veículo. Aqui a linha SAI mesmo, ao contrário de conta e usuário: um
 * bloqueio removido não é fato do passado a preservar, é ocupação que deixou de
 * existir — e mantê-la com uma flag faria toda consulta de disponibilidade
 * carregar um filtro que alguém vai esquecer. O instantâneo fica no `audit_log`.
 */
export function useRemoverBloqueio(): UseMutationResult<BloqueioDeVeiculo, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiDelete(ROTAS_DE_DISPONIBILIDADE.bloqueioPorId(id), bloqueioDeVeiculoSchema),
    onSuccess: () => {
      invalidarDisponibilidade(queryClient, CHAVE_DE_BLOQUEIOS)
    },
  })
}

// ---------------------------------------------------------------------------
// Prestadores
// ---------------------------------------------------------------------------

export function usePrestadores(filtros: FiltrosDePrestadores): UseQueryResult<PaginaDePrestadores> {
  return useQuery({
    queryKey: [CHAVE_DE_PRESTADORES, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        tipo: filtros.tipo,
        status: filtros.status,
        locationId: filtros.locationId,
        livreDe: filtros.livreDe,
        livreAte: filtros.livreAte,
      })

      return apiGet(`${ROTAS_DE_DISPONIBILIDADE.prestadores}${query}`, paginaDePrestadoresSchema, {
        signal,
      })
    },
    placeholderData: (anterior) => anterior,
  })
}

export function usePrestador(id: string | null): UseQueryResult<Prestador> {
  return useQuery({
    queryKey: [CHAVE_DE_PRESTADORES, id],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_DISPONIBILIDADE.prestadorPorId(id ?? ''), prestadorSchema, { signal }),
    enabled: id !== null,
  })
}

export function useCriarPrestador(): UseMutationResult<Prestador, Error, CorpoDePrestador> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDePrestador) =>
      apiPost(ROTAS_DE_DISPONIBILIDADE.prestadores, prestadorSchema, dados),
    onSuccess: () => {
      invalidarDisponibilidade(queryClient, CHAVE_DE_PRESTADORES)
    },
  })
}

export interface AtualizacaoDePrestador {
  readonly id: string
  readonly dados: CorpoDePrestador
}

/**
 * Edita o prestador — inclusive o `status`, que é como se REATIVA quem foi
 * inativado. Um motorista que volta muda quem está livre no período, e por isso
 * a cotação sai do cache junto: um extra recusado por `SEM_PRESTADOR` pode ter
 * deixado de ser recusado.
 */
export function useAtualizarPrestador(): UseMutationResult<
  Prestador,
  Error,
  AtualizacaoDePrestador
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDePrestador) =>
      apiPatch(ROTAS_DE_DISPONIBILIDADE.prestadorPorId(id), prestadorSchema, dados),
    onSuccess: () => {
      invalidarDisponibilidade(queryClient, CHAVE_DE_PRESTADORES)
    },
  })
}

/**
 * Inativa o prestador. NÃO apaga, ao contrário do bloqueio: a escala passada
 * aponta para ele e o relatório de rentabilidade soma o custo diário dele em
 * locações já faturadas. Os bloqueios FUTUROS continuam de pé de propósito —
 * inativar é decisão de cadastro; cancelar compromisso assumido é outra, tomada
 * bloqueio a bloqueio.
 */
export function useInativarPrestador(): UseMutationResult<Prestador, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiDelete(ROTAS_DE_DISPONIBILIDADE.prestadorPorId(id), prestadorSchema),
    onSuccess: () => {
      invalidarDisponibilidade(queryClient, CHAVE_DE_PRESTADORES, CHAVE_DE_ESCALA)
    },
  })
}

// ---------------------------------------------------------------------------
// Escala dos prestadores
// ---------------------------------------------------------------------------

export function useEscala(filtros: FiltrosDeBloqueios): UseQueryResult<PaginaDeEscala> {
  return useQuery({
    queryKey: [CHAVE_DE_ESCALA, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        serviceProviderId: filtros.serviceProviderId,
        motivo: filtros.motivo,
        de: filtros.de,
        ate: filtros.ate,
      })

      return apiGet(`${ROTAS_DE_DISPONIBILIDADE.escala}${query}`, paginaDeEscalaSchema, { signal })
    },
    placeholderData: (anterior) => anterior,
  })
}

export function useCriarBloqueioDePrestador(): UseMutationResult<
  BloqueioDePrestador,
  Error,
  CorpoDeBloqueio
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeBloqueio) =>
      apiPost(ROTAS_DE_DISPONIBILIDADE.escala, bloqueioDePrestadorSchema, dados),
    onSuccess: () => {
      // O prestador carrega a contagem de bloqueios da escala.
      invalidarDisponibilidade(queryClient, CHAVE_DE_ESCALA, CHAVE_DE_PRESTADORES)
    },
  })
}

/**
 * Libera a agenda. Não há `PATCH` de escala, e é deliberado: mover o turno de
 * uma pessoa é a mesma decisão de mover um bloqueio para outro carro — a
 * constraint precisa avaliar o intervalo novo inteiro. Quem errou o horário
 * apaga e cria.
 */
export function useRemoverBloqueioDePrestador(): UseMutationResult<
  BloqueioDePrestador,
  Error,
  string
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiDelete(ROTAS_DE_DISPONIBILIDADE.escalaPorId(id), bloqueioDePrestadorSchema),
    onSuccess: () => {
      invalidarDisponibilidade(queryClient, CHAVE_DE_ESCALA, CHAVE_DE_PRESTADORES)
    },
  })
}

// ---------------------------------------------------------------------------
// Lista de espera
// ---------------------------------------------------------------------------

export function useWaitlist(filtros: FiltrosDeWaitlist): UseQueryResult<PaginaDeWaitlist> {
  return useQuery({
    queryKey: [CHAVE_DE_WAITLIST, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        categoryId: filtros.categoryId,
        accountId: filtros.accountId,
        status: filtros.status,
        de: filtros.de,
        ate: filtros.ate,
      })

      return apiGet(`${ROTAS_DE_COTACOES.listaDeEspera}${query}`, paginaDeWaitlistSchema, {
        signal,
      })
    },
    placeholderData: (anterior) => anterior,
  })
}

/**
 * Registra a intenção quando não há carro.
 *
 * Não invalida disponibilidade: entrar na fila não ocupa nada — é justamente o
 * contrário, é a demanda que a frota atual NÃO atendeu. O que muda é a própria
 * fila, e é ela que sai do cache.
 */
export function useCriarItemDeWaitlist(): UseMutationResult<
  ItemDeWaitlist,
  Error,
  CorpoDeItemDeWaitlist
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeItemDeWaitlist) =>
      apiPost(ROTAS_DE_COTACOES.listaDeEspera, itemDeWaitlistSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_WAITLIST] })
    },
  })
}

// ---------------------------------------------------------------------------
// Overbooking
// ---------------------------------------------------------------------------

export function useOverbooking(filtros: FiltrosDeOverbooking): UseQueryResult<PaginaDeOverbooking> {
  return useQuery({
    queryKey: [CHAVE_DE_OVERBOOKING, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        categoriaVendidaId: filtros.categoriaVendidaId,
        de: filtros.de,
        ate: filtros.ate,
      })

      return apiGet(`${ROTAS_DE_COTACOES.overbooking}${query}`, paginaDeOverbookingSchema, {
        signal,
      })
    },
    placeholderData: (anterior) => anterior,
  })
}

export function useRegistrarOverbooking(): UseMutationResult<
  Overbooking,
  Error,
  CorpoDeOverbooking
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeOverbooking) =>
      apiPost(ROTAS_DE_COTACOES.overbooking, overbookingSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_OVERBOOKING] })
    },
  })
}

// ---------------------------------------------------------------------------
// Apresentação
// ---------------------------------------------------------------------------

/**
 * `2 de 8` — a leitura que a tela precisa dar da capacidade.
 *
 * Os dois números juntos, sempre: "2 disponíveis" esconde se a frota tem duas
 * ou oitenta unidades, e é essa diferença que decide entre vender e comprar
 * carro.
 */
export function disponibilidadeEmTexto(item: {
  readonly disponiveis: number
  readonly capacidade: number
}): string {
  return `${String(item.disponiveis)} de ${String(item.capacidade)}`
}

/**
 * A cotação de uma categoria só tem preço quando `cotacao` veio preenchida —
 * categoria indisponível traz `null` ali e ainda assim traz `diarias`. Este
 * predicado existe para a tela não escrever `R$ —` onde deveria escrever o
 * motivo.
 */
export function temPreco(item: CategoriaCotada): boolean {
  return item.cotacao !== null
}

/**
 * O status da cotação já expirada, calculado pela data.
 *
 * O servidor devolve `ABERTA` no instante da emissão e não fica varrendo a
 * tabela para envelhecer linha — quem vê a cotação vencer é a tela, minutos
 * depois, com o `expiraEm` na mão. Sem isto, uma cotação de ontem apareceria
 * "aberta" para o atendente que fosse convertê-la.
 */
export function statusVigenteDaCotacao(cotacao: {
  readonly status: StatusDaCotacao
  readonly expiraEm: string
}): StatusDaCotacao {
  if (cotacao.status !== 'ABERTA') {
    return cotacao.status
  }

  const expiraEm = new Date(cotacao.expiraEm)

  if (Number.isNaN(expiraEm.getTime())) {
    return cotacao.status
  }

  return expiraEm.getTime() <= Date.now() ? 'EXPIRADA' : 'ABERTA'
}
